import { useContext, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { TOAST_NOTIFICATION } from 'utils/constants/dataTest';
import { logError } from 'utils/helpers';
import StoreApi from './StoreApi';
import { ShippingSettings } from './types';
import CONTENT from './content';
import { NotificationContext } from '../../../containers/Notification';
import { UserContext } from '../../../components/Layout';

// Shipping Data
const SHIPPING_INFO_URI = `/v1/settings/shipping_address`;

export const useAccountSettings = () => {
  const { setNotificationData } = useContext(NotificationContext);
  const { loggedIn, isMember } = useContext(UserContext);
  const [shippingAddress, setShippingAddress] =
    useState<ShippingSettings | null>(null);
  const [loadingShippingInfo, setLoadingShippingInfo] = useState(false);
  const [savingShippingInfo, setSavingShippingInfo] = useState(false);
  const [savedShippingInfo, setSavedShippingInfo] = useState(false);
  const [invalidAddress, setInvalidAddress] = useState(false);

  const getShippingAddress = async () => {
    try {
      setLoadingShippingInfo(true);
      const { data } = await StoreApi.get(SHIPPING_INFO_URI);
      setShippingAddress({
        ...data,
        address1: data.street1,
        address2: data.street2,
        first_name: data.first_name,
        last_name: data.last_name,
      });
      return data;
    } catch (err) {
      setNotificationData({
        message: CONTENT.notifications.fetchFailed.title,
        persist: false,
        details: CONTENT.notifications.fetchFailed.message,
        type: TOAST_NOTIFICATION.ERROR_NOTIFICATION,
        source: ANALYTICS.LABELS.ORDER_MODAL,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Fetch Failed',
          component: ANALYTICS.LABELS.ORDER_MODAL,
        },
      });
      logError(err.message, {
        component: 'ShippingSettings',
        method: 'loadInitialState',
      });
      return Promise.reject(err);
    } finally {
      setLoadingShippingInfo(false);
    }
  };

  const updateShippingAddress = async (shipping_address: ShippingSettings) => {
    const payload = { shipping_address };
    await analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.ORDER_MODAL_SAVE.label,
        description: ANALYTICS.LABELS.ORDER_MODAL_SAVE.description,
      },
    });
    try {
      setSavingShippingInfo(true);
      const { data } = await StoreApi.post(SHIPPING_INFO_URI, payload);
      setShippingAddress({
        ...data,
        address1: data.street1,
        address2: data.street2,
        first_name: data.first_name,
        last_name: data.last_name,
      });
      setInvalidAddress(false);
      setSavedShippingInfo(true);
      setNotificationData({
        message: CONTENT.notifications.updateSuccess.message,
        persist: false,
        source: ANALYTICS.LABELS.SHIPPING_INFORMATION,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_SUCCESS,
        data: {
          label: CONTENT.notifications.updateSuccess.message,
          component: ANALYTICS.LABELS.SHIPPING_INFORMATION,
        },
      });
      return data;
    } catch (err) {
      const invalidAddress = err?.response?.status === 422;
      const invalidAddressMsg =
        err.response?.data?.message === 'Address is invalid';

      setInvalidAddress(invalidAddress && invalidAddressMsg);

      if (invalidAddress && invalidAddressMsg)
        throw new Error(err.response?.data?.message);

      setNotificationData({
        message: CONTENT.notifications.updateFailed.title,
        persist: false,
        details: CONTENT.notifications.updateFailed.message,
        type: TOAST_NOTIFICATION.ERROR_NOTIFICATION,
        source: ANALYTICS.LABELS.ORDER_MODAL,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Update Failed',
          component: ANALYTICS.LABELS.ORDER_MODAL,
        },
      });
      logError(err.message, {
        component: 'ShippingSettings',
        method: 'updateShippingInformation',
      });
      return Promise.reject(err);
    } finally {
      setSavingShippingInfo(false);
    }
  };

  const resetState = () => {
    setSavedShippingInfo(false);
  };

  useEffect(() => {
    if (isMember && !shippingAddress) getShippingAddress();
  }, [loggedIn]);

  return {
    getShippingAddress,
    updateShippingAddress,
    loadingShippingInfo,
    savingShippingInfo,
    shippingAddress,
    invalidAddress,
    savedShippingInfo,
    resetState,
  };
};

export default useAccountSettings;
